<template>
	<v-card class="location-container pb-1" elevation="0" color="transparent">
		<v-card
			class="location-card"
			elevation="0"
			outlined
		>
			<!-- Location unavailable message -->
			<div v-if="!hasValidCoordinates" class="pa-6 text-center">
				<v-icon size="64" color="grey lighten-1">mdi-map-marker-off</v-icon>
				<div class="text-body-1 mt-3 grey--text text--darken-1">
					Posizione condivisa
				</div>
				<div class="text-caption grey--text mt-2">
					Le coordinate non sono disponibili per messaggi sincronizzati
				</div>
			</div>

			<!-- Map preview (only if coordinates are valid) -->
			<template v-else>
				<div class="map-preview" @click="openInGoogleMaps">
					<iframe
						:src="osmEmbedUrl"
						width="100%"
						height="200"
						frameborder="0"
						scrolling="no"
						style="border-radius: 4px 4px 0 0; pointer-events: none;"
					></iframe>
					<div class="map-overlay">
						<v-icon large color="white">mdi-map-marker</v-icon>
					</div>
				</div>

				<v-card-text class="pa-3">
					<div class="d-flex align-center mb-2">
						<v-icon color="primary" class="mr-2">mdi-map-marker</v-icon>
						<div class="text-body-2 text--secondary">
							{{ formattedCoordinates }}
						</div>
					</div>
					<div v-if="locationData.address" class="text-body-2 ml-8">
						{{ locationData.address }}
					</div>
					<div v-if="locationData.name" class="text-body-2 font-weight-medium ml-8">
						{{ locationData.name }}
					</div>
				</v-card-text>

				<v-divider></v-divider>

				<v-card-actions class="pa-2">
					<v-btn
						small
						text
						color="primary"
						@click="openInGoogleMaps"
					>
						<v-icon small left>mdi-google-maps</v-icon>
						Google Maps
					</v-btn>
					<v-spacer></v-spacer>
					<v-btn
						small
						text
						color="primary"
						@click="copyCoordinates"
					>
						<v-icon small left>mdi-content-copy</v-icon>
						Copia coordinate
					</v-btn>
				</v-card-actions>
			</template>
		</v-card>
	</v-card>
</template>

<script>
	export default {
		name: 'LocationMessage',
		props: {
			message: {
				type: Object,
				required: true
			}
		},

		computed: {
			locationData() {
				return this.message.location_data || {}
			},
			latitude() {
				return parseFloat(this.locationData.latitude)
			},
			longitude() {
				return parseFloat(this.locationData.longitude)
			},
			hasValidCoordinates() {
				return !isNaN(this.latitude) && !isNaN(this.longitude) &&
					   this.latitude !== 0 && this.longitude !== 0 &&
					   !this.locationData.unavailable
			},
			formattedCoordinates() {
				if (!this.hasValidCoordinates) return 'Coordinate non disponibili'
				return `${this.latitude.toFixed(6)}, ${this.longitude.toFixed(6)}`
			},
			googleMapsUrl() {
				if (!this.hasValidCoordinates) return '#'
				return `https://www.google.com/maps?q=${this.latitude},${this.longitude}`
			},
			osmEmbedUrl() {
				if (!this.hasValidCoordinates) return ''
				const bbox = this.calculateBoundingBox(this.latitude, this.longitude, 0.01)
				return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${this.latitude},${this.longitude}`
			}
		},
		
		methods: {
			calculateBoundingBox(lat, lon, delta) {
				const minLon = lon - delta
				const minLat = lat - delta
				const maxLon = lon + delta
				const maxLat = lat + delta
				return `${minLon},${minLat},${maxLon},${maxLat}`
			},
			openInGoogleMaps() {
				if (!this.hasValidCoordinates) {
					this.$store.commit('setSnackbar', {
						timeout: 3000,
						text: 'Coordinate non disponibili per questa posizione',
						value: true,
						color: 'warning'
					})
					return
				}
				window.open(this.googleMapsUrl, '_blank')
			},
			copyCoordinates() {
				if (!this.hasValidCoordinates) {
					this.$store.commit('setSnackbar', {
						timeout: 3000,
						text: 'Coordinate non disponibili per questa posizione',
						value: true,
						color: 'warning'
					})
					return
				}

				const coords = `${this.latitude},${this.longitude}`

				if (navigator.clipboard && navigator.clipboard.writeText) {
					navigator.clipboard.writeText(coords).then(() => {
						this.$store.commit('setSnackbar', {
							timeout: 2000,
							text: 'Coordinate copiate',
							value: true,
							color: 'success'
						})
					}).catch(() => {
						this.fallbackCopyToClipboard(coords)
					})
				} else {
					this.fallbackCopyToClipboard(coords)
				}
			},
			fallbackCopyToClipboard(text) {
				const textArea = document.createElement('textarea')
				textArea.value = text
				textArea.style.position = 'fixed'
				textArea.style.left = '-999999px'
				document.body.appendChild(textArea)
				textArea.select()

				try {
					document.execCommand('copy')
					this.$store.commit('setSnackbar', {
						timeout: 2000,
						text: 'Coordinate copiate',
						value: true,
						color: 'success'
					})
				} catch (err) {
					this.$store.commit('setSnackbar', {
						timeout: 2000,
						text: 'Errore durante la copia',
						value: true,
						color: 'error'
					})
				}

				document.body.removeChild(textArea)
			}
		}
	}
</script>

<style scoped>
	.location-container {
		max-width: 400px
	}

	.location-card {
		border-radius: 8px;
		border: 1px solid #e0e0e0
	}

	.map-preview {
		position: relative;
		cursor: pointer;
		overflow: hidden
	}

	.map-preview:hover .map-overlay {
		opacity: 1
	}

	.map-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.3);
		display: flex;
		align-items: center;
		justify-content: center;
		opacity: 0;
		transition: opacity 0.2s ease
	}
</style>
